import { useState, useEffect, useCallback, useRef } from 'react';
import useSelectedDomain from '../useSelectedDomain';
import { getIssuersSummary } from '../../api/IssuerService';

export const useIssuersSummary = (initialFilters = {}) => {
  const [issuers, setIssuers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSearching, setIsSearching] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    pageNumber: 1,
    pageSize: 10,
    totalCount: 0,
    totalPages: 0,
    hasPreviousPage: false,
    hasNextPage: false
  });
  
  const [filters, setFilters] = useState({
    issuerName: '',
    sortBy: 'IssuerName',
    sortDescending: false,
    ...initialFilters
  });
  
  const selectedDomain = useSelectedDomain();
  const domainId = selectedDomain?.id;
  
  const searchTimeoutRef = useRef(null);
  const abortControllerRef = useRef(null);
  const isMountedRef = useRef(true);

  const loadIssuers = useCallback(async (pageNumber = 1, searchFilters = filters, isSilentSearch = false) => {
    if (!domainId) {
      if (isMountedRef.current) {
        setIssuers([]);
        setLoading(false);
      }
      return;
    }

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    abortControllerRef.current = new AbortController();
    
    // Pour les recherches silencieuses, on n'affiche pas l'indicateur
    if (!isSilentSearch) {
      setIsSearching(true);
    }
    
    if (pageNumber === 1 && !isSilentSearch) {
      setLoading(true);
    }
    
    setError(null);
    
    try {
      const result = await getIssuersSummary(
        domainId, 
        { ...searchFilters, pageNumber },
        abortControllerRef.current.signal
      );

      if (isMountedRef.current) {
        setIssuers(result.data || []);
        setPagination(result.pagination || {
          pageNumber: pageNumber,
          pageSize: 10,
          totalCount: 0,
          totalPages: 0,
          hasPreviousPage: false,
          hasNextPage: false
        });
        
        // SUPPRIMÉ : Les toasts d'information
        // if ((result.data || []).length === 0 && searchFilters.issuerName) {
        //   toast.info(`Aucun émetteur trouvé pour "${searchFilters.issuerName}"`);
        // }
      }
    } catch (err) {
      if (err.name === 'AbortError' || err.message === 'SearchCancelled') {
        return;
      }
      
      if (isMountedRef.current) {
        setError(err.message || 'Erreur lors du chargement des émetteurs');
        
        // SUPPRIMÉ : Les toasts d'erreur
        // if (err.response?.status !== 404) {
        //   toast.error('Erreur lors du chargement des émetteurs');
        // }
      }
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
        setIsSearching(false);
      }
    }
  }, [domainId]);

  const handleFilterChange = useCallback((newFilters) => {
    const updatedFilters = { ...filters, ...newFilters };
    setFilters(updatedFilters);
    
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    // Recherche silencieuse - pas d'indicateur pour les petites recherches
    const isSilentSearch = !newFilters.issuerName || newFilters.issuerName.length < 3;
    
    searchTimeoutRef.current = setTimeout(() => {
      loadIssuers(1, updatedFilters, isSilentSearch);
    }, 400);
  }, [filters, loadIssuers]);

  const handleResetFilters = useCallback(() => {
    const resetFilters = {
      issuerName: '',
      sortBy: 'IssuerName',
      sortDescending: false
    };
    
    setFilters(resetFilters);
    loadIssuers(1, resetFilters);
    
    // SUPPRIMÉ : Toast de confirmation
    // toast.info('Filtres réinitialisés');
  }, [loadIssuers]);

  const handlePageChange = useCallback((pageNumber, newPageSize = pagination.pageSize) => {
    loadIssuers(pageNumber, { ...filters, pageSize: newPageSize });
  }, [filters, pagination.pageSize, loadIssuers]);

  useEffect(() => {
    if (domainId) {
      loadIssuers();
    } else if (isMountedRef.current) {
      setIssuers([]);
      setLoading(false);
    }
  }, [domainId]);

  useEffect(() => {
    isMountedRef.current = true;
    
    return () => {
      isMountedRef.current = false;
      
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
      
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  return {
    issuers,
    loading,
    isSearching,
    error,
    pagination,
    filters,
    setFilters: handleFilterChange,
    loadIssuers,
    handleResetFilters,
    handlePageChange
  };
};